import React from 'react';
import type { TemplateProfile } from '../../../templates/types';
import { makeStyles, tokens, Text } from '@fluentui/react-components';

const useStyles = makeStyles({
  card: {
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: `inset 0 0 0 2px ${tokens.colorNeutralStroke2}`,
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px',
    minHeight: '160px',
    width: '100%',
    transition: 'box-shadow 0.15s',
    ':hover': {
      boxShadow: `${tokens.shadow4}, inset 0 0 0 2px ${tokens.colorNeutralStroke1}`,
    },
  },
  selected: {
    border: `2px solid #2B579A`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: '#eef3fb',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '8px',
    width: '100%',
  },
  thumbnail: {
    width: '100%',
    height: 'auto',
    minHeight: '100px',
    backgroundColor: '#ffffff',
    borderRadius: '2px',
    overflow: 'hidden',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  thumbnailImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  },
  name: {
    fontSize: '12px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    textAlign: 'center',
  },
  version: {
    fontSize: '11px',
    color: tokens.colorNeutralForeground3,
    textAlign: 'center',
  },
});

interface Props {
  profile: TemplateProfile;
  isActive: boolean;
  onSelect: () => void;
}

export default function TemplateCard({ profile, isActive, onSelect }: Props) {
  const styles = useStyles();

  const onKeyDown: React.KeyboardEventHandler<HTMLDivElement> = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      aria-pressed={isActive}
      onClick={onSelect}
      onKeyDown={onKeyDown}
      className={`${styles.card} ${isActive ? styles.selected : ''}`}
      aria-label={`Select template ${profile.name}`}
    >
      {profile.thumbnail ? (
        <div className={styles.thumbnail} aria-hidden="true">
          <img className={styles.thumbnailImage} src={profile.thumbnail} alt="" />
        </div>
      ) : null}
      <Text className={styles.name}>{profile.name}</Text>
      <Text className={styles.version}>{profile.version}</Text>
    </div>
  );
}
